import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  // After /auth/login returns mfaRequired we hold the user partly authed.
  const [mfaPending, setMfaPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const status = await api.auth.status();
      setSetupRequired(status.setupRequired);
      if (!status.setupRequired) {
        try {
          const me = await api.auth.me();
          setUser(me);
          setMfaPending(false);
        } catch (err) {
          if (err.status !== 401) throw err;
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (err) {
      console.warn('Auth refresh failed', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const res = await api.auth.login({ email, password });
    if (res.mfaRequired) {
      setMfaPending(true);
      return { mfaRequired: true };
    }
    setUser(res.user);
    setMfaPending(false);
    return { user: res.user };
  };

  const verifyMfa = async (code) => {
    await api.mfa.loginVerify(code);
    const me = await api.auth.me();
    setUser(me);
    setMfaPending(false);
    return me;
  };

  const loginWithPasskey = async (passkeyResponse) => {
    const res = await api.passkey.loginVerify({ response: passkeyResponse });
    const me = await api.auth.me();
    setUser(me);
    setMfaPending(false);
    return res.user || me;
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      setMfaPending(false);
    }
  };

  const completeSetup = async (form) => {
    const res = await api.auth.setup(form);
    setSetupRequired(false);
    setUser(res.user);
    return res.user;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        setupRequired,
        mfaPending,
        refresh,
        login,
        verifyMfa,
        loginWithPasskey,
        logout,
        completeSetup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
